import { Component, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { Camera } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { ModalController, IonicModule } from '@ionic/angular';
import { BrowserMultiFormatReader, Result } from '@zxing/library';
import { Button } from './button.interface';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-qr-code-scanner',
    templateUrl: './qr-code-scanner.component.html',
    styleUrls: ['./qr-code-scanner.component.scss'],
    imports: [IonicModule, TranslatePipe]
})
export class QrCodeScannerComponent {
  modalController = inject(ModalController);

  public permission: boolean = true;
  public availableCameras: MediaDeviceInfo[] = [];
  public selectedCamera: MediaDeviceInfo;
  public inScanTimeout: boolean = false;

  private currentMediaStream: MediaStream;
  private codeReader: BrowserMultiFormatReader;
  private videoElement: HTMLVideoElement;

  @ViewChild('modal', {static: true})
  private modal: HTMLIonModalElement;
  @ViewChild('camSelect')
  private camSelect: HTMLIonSelectElement;

  public isScannerOpen: boolean = false;
  @Input() set scannerOpen(value: boolean) {
    if (value) {
      this.init();
    } else {
      this.closeModal();
    }
    this.isScannerOpen = value;
  }

  @Input()
  public scannerEnable: boolean = true;
  @Input()
  public scanTimeout: number = 500;
  @Input()
  public buttons: Button[] = [];
  @Output()
  scanSuccessEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  scannerDismissEvent: EventEmitter<void> = new EventEmitter<void>();

  private init() {
    this.modal.onDidDismiss().then(() => {
      this.scannerDismissEvent.emit();
      this.closeMediaStream(this.currentMediaStream);
      this.codeReader?.stopContinuousDecode();
      this.videoElement.remove();
    });

    if(Capacitor.isNativePlatform()) {
      Camera.requestPermissions({permissions: ['camera']}).then(permissionStatus => {
        if(permissionStatus.camera !== "granted") {
          this.permission = false;
        }
        else {
          this.setupScanner();
        }
      });
    }
    else {
      navigator.permissions.query({name: "camera" as PermissionName}).then(permissionStatus => {
        if(permissionStatus.state !== 'granted') {
          this.permission = false;
        }
        // The permission prompt is created when asking for the UserMedia to get the MediaStream. So we need to start the setup process.
        this.setupScanner();
      });
    }
  }

  private setupScanner() {
    this.modalController.getTop().then(element => {
      // HTML video element preview setup
      this.videoElement = document.createElement("video");
      this.videoElement.classList.add("scannerPreview");
      element.appendChild(this.videoElement);

      // Video scanner setup
      navigator.mediaDevices.getUserMedia({video: {facingMode: "environment" }}).then(stream => {
        this.permission = true;

        // By default, a stream has only one VideoTrack and one AudioTrack
        this.setAvailableCameras(stream.getVideoTracks()[0].getSettings().deviceId);
        this.setNewMediaStream(stream);

        this.codeReader = new BrowserMultiFormatReader(undefined, 1000);
        this.codeReader.decodeFromVideoContinuously(this.videoElement, null, this.scanSuccess.bind(this));
      });
    });
  }

  closeModal() {
    if(this.modal?.isOpen) {
      this.modal.dismiss();
    }
  }

  async scanSuccess(scanResult: Result) {
    if(scanResult) {
      if(!this.inScanTimeout && this.scannerEnable) {
        this.inScanTimeout = true;
        this.scanSuccessEvent.emit(scanResult.getText());
        setTimeout(() => this.inScanTimeout = false, this.scanTimeout);
      }
    }
  }

  public selectCamera(event) {
    this.selectedCamera = event.detail.value;
    this.applyCameraChange();
  }

  public changeCamera() {
    if(this.availableCameras.length == 2) {
      this.selectedCamera = this.availableCameras.filter(cam => cam.deviceId != this.selectedCamera.deviceId)[0];
      this.applyCameraChange();
    }
    else {
      this.camSelect.open();
    }
  }

  private applyCameraChange() {
    this.closeMediaStream(this.currentMediaStream);
    navigator.mediaDevices.getUserMedia({video: {deviceId: {exact: this.selectedCamera.deviceId}}})
    .then(stream => this.setNewMediaStream(stream));
  }

  private setAvailableCameras(cameraIdInUse?: string) {
    navigator.mediaDevices.enumerateDevices().then(devices => {
      this.availableCameras = devices.filter(device => device.kind === 'videoinput');
      if (cameraIdInUse) {
        this.selectedCamera = this.availableCameras.filter(cam => cam.deviceId === cameraIdInUse)[0];
      }
    });
  }

  private closeMediaStream(stream: MediaStream) {
    stream?.getTracks().forEach(track => track.stop());
  }

  private setNewMediaStream(stream: MediaStream) {
    this.currentMediaStream = stream;
    this.videoElement.srcObject = this.currentMediaStream;
  }

  protected readonly console = console;
}
